import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { CodeBlock, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';
import tutorialStyles from './Tutorial.module.css';

export const Route = createFileRoute('/documentation/tutorial-angular')({
  beforeLoad: () => {
    throw redirect({ to: '/documentation/tutorial' });
  },
  component: TutorialAngularPage,
});

function TutorialAngularPage() {
  return (
    <div className={tutorialStyles.page}>
      <div className={styles.breadcrumb}>Getting Started</div>
      <h1 className={styles.pageTitle}>Stream in Angular</h1>
      <p className={styles.pageSubtitle}>
        Add a GPU-rendered stream to an Angular app in about five minutes.
      </p>

      <div className={tutorialStyles.badge}>
        <span>~5 min</span>
        <span className={tutorialStyles.badgeSep} />
        <span>3 steps</span>
      </div>

      {/* ── Video placeholder ── */}
      <div className={tutorialStyles.videoPlaceholder}>
        <div className={tutorialStyles.videoPlayIcon}>
          <div className={tutorialStyles.videoPlayTriangle} />
        </div>
        <span className={tutorialStyles.videoLabel}>
          Watch the framework walkthrough (4:30)
        </span>
      </div>

      {/* ── Prerequisites ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="prerequisites">
          Before You Begin
        </h2>
        <ul className={tutorialStyles.prereqList}>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              <span className={styles.emphasis}>Node.js 18+</span> and npm
            </span>
          </li>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              An <span className={styles.emphasis}>admission token</span> — see
              the{' '}
              <Link to="/documentation/tutorial" hash="prerequisites">
                HTML tutorial prerequisites
              </Link>
            </span>
          </li>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              Familiarity with Angular components (standalone or NgModule)
            </span>
          </li>
        </ul>
        <InfoCallout variant="tip">
          New to pixel-stream? Start with the{' '}
          <Link to="/documentation/tutorial">HTML tutorial</Link> first — it
          covers the core concepts without any framework overhead.
        </InfoCallout>
      </section>

      {/* ── Step 1 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="scaffold-the-project">
          Step 1 — Scaffold the Project
        </h2>
        <p className={styles.prose}>
          Create a new Angular project and install the pixel-stream package.
          The flags keep the setup minimal:
        </p>
        <CodeBlock language="bash" title="Terminal">
{`npx @angular/cli new my-stream --style=css --ssr=false --routing=false
cd my-stream
npm install @interlucent/pixel-stream`}
        </CodeBlock>
        <InfoCallout variant="info">
          Angular's scaffolding takes a bit longer than React or Vue — about
          60–90 seconds for <code>ng new</code>. This is normal.
        </InfoCallout>
      </section>

      {/* ── Step 2 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="add-the-stream">
          Step 2 — Add the Stream Component
        </h2>
        <p className={styles.prose}>
          Angular requires <code>CUSTOM_ELEMENTS_SCHEMA</code> to accept
          unknown HTML elements in templates. Replace{' '}
          <code>src/app/app.component.ts</code> with:
        </p>
        <CodeBlock language="typescript" title="src/app/app.component.ts">
{`import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import '@interlucent/pixel-stream';

@Component({
  selector: 'app-root',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: \`
    <pixel-stream
      #ps
      style="display: block; width: 100vw; height: 100vh"
    ></pixel-stream>
  \`,
})
export class AppComponent implements AfterViewInit {
  @ViewChild('ps') psRef!: ElementRef<HTMLElement>;

  ngAfterViewInit() {
    (this.psRef.nativeElement as any).admissionToken = 'YOUR_TOKEN';
  }
}`}
        </CodeBlock>
        <p className={styles.prose}>
          The <code>import</code> registers the custom element.{' '}
          <code>CUSTOM_ELEMENTS_SCHEMA</code> tells Angular's template compiler
          to accept <code>&lt;pixel-stream&gt;</code> as a valid element.{' '}
          <code>@ViewChild</code> provides a typed reference for property
          access and method calls.
        </p>
        <InfoCallout variant="info">
          Using NgModule instead of standalone? Add{' '}
          <code>CUSTOM_ELEMENTS_SCHEMA</code> to your module's{' '}
          <code>schemas</code> array and the <code>import</code> statement to
          your component or <code>main.ts</code>.
        </InfoCallout>
      </section>

      {/* ── Step 3 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="run-and-play">
          Step 3 — Run and Play
        </h2>
        <CodeBlock language="bash" title="Terminal">
{`ng serve`}
        </CodeBlock>
        <p className={styles.prose}>
          Open <code>http://localhost:4200</code>. You will see a dark viewport
          with a <span className={styles.emphasis}>play button</span>. Click
          it — the component connects to the platform, requests a GPU worker,
          and starts streaming. Move your mouse to interact. That's it — you're
          streaming from Angular.
        </p>
        <InfoCallout variant="warning">
          If nothing appears, open the browser console (F12). The most common
          issue is an expired admission token — generate a fresh one and
          replace the value in <code>app.component.ts</code>.
        </InfoCallout>
      </section>

      {/* ── Make It Yours ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="make-it-yours">
          Make It Yours
        </h2>

        <h3 className={styles.subsectionTitle} id="custom-overlay">
          Custom Overlay
        </h3>
        <p className={styles.prose}>
          Replace the built-in controls with your own Angular template using
          the <code>overlay</code> slot. The <code>&lt;pixel-stream&gt;</code>{' '}
          element sets a <code>status</code> attribute on itself as the session
          progresses — <code>idle</code>, <code>connecting</code>,{' '}
          <code>queued</code>, <code>streaming</code>, <code>failed</code>,
          etc. Use CSS attribute selectors to show the right state:
        </p>
        <CodeBlock language="html" title="app.component.html">
{`<pixel-stream #ps>
  <div slot="overlay" class="overlay">
    <div class="state state-idle">
      <button (click)="play()">Launch Experience</button>
    </div>
    <div class="state state-pending">
      <div class="spinner"></div>
      <p>Preparing your session&hellip;</p>
      <button (click)="cancel()">Cancel</button>
    </div>
    <div class="state state-error">
      <p>Something went wrong.</p>
      <button (click)="play()">Try Again</button>
    </div>
  </div>
</pixel-stream>`}
        </CodeBlock>
        <CodeBlock language="css" title="app.component.css">
{`.state { display: none; }

pixel-stream[status="idle"]           .state-idle,
pixel-stream[status="connected"]      .state-idle    { display: flex; }

pixel-stream[status="connecting"]     .state-pending,
pixel-stream[status="authenticating"] .state-pending,
pixel-stream[status="queued"]         .state-pending,
pixel-stream[status="negotiating"]    .state-pending { display: flex; }

pixel-stream[status="failed"]        .state-error,
pixel-stream[status="ended"]         .state-error   { display: flex; }

pixel-stream[status="streaming"]     .overlay { display: none; }`}
        </CodeBlock>
        <InfoCallout variant="info">
          Angular's default <code>ViewEncapsulation.Emulated</code> scopes CSS
          to the component, which prevents the{' '}
          <code>pixel-stream[status]</code> selector from matching. Set{' '}
          <code>{'encapsulation: ViewEncapsulation.None'}</code> on the
          component, or use <code>::ng-deep</code> to pierce the encapsulation.
          See the{' '}
          <Link to="/documentation/streaming-guide" hash="custom-overlay">
            Streaming Guide
          </Link>{' '}
          for the full overlay pattern.
        </InfoCallout>

        <h3 className={styles.subsectionTitle} id="listen-for-events">
          Listen for Events
        </h3>
        <p className={styles.prose}>
          Use <code>addEventListener</code> on the ViewChild ref in{' '}
          <code>ngAfterViewInit</code>:
        </p>
        <CodeBlock language="typescript" title="events via ViewChild">
{`ngAfterViewInit() {
  const el = this.psRef.nativeElement;

  el.addEventListener('status-change', (e: Event) => {
    const { newStatus } = (e as CustomEvent).detail;
    console.log('Status:', newStatus);
  });

  el.addEventListener('data-channel-open', () => {
    console.log('Data channel ready');
  });
}`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="send-a-message">
          Send a Message
        </h3>
        <CodeBlock language="typescript" title="messaging via ViewChild">
{`ngAfterViewInit() {
  const el = this.psRef.nativeElement;

  el.addEventListener('data-channel-open', () => {
    (el as any).sendUIInteraction({
      action: 'setColor',
      target: 'car-body',
      color: '#FF5500',
    });
  });
}`}
        </CodeBlock>
        <InfoCallout variant="warning">
          Always wait for the <code>data-channel-open</code> event before
          calling <code>sendUIInteraction()</code> or{' '}
          <code>sendCommand()</code>.
        </InfoCallout>
      </section>

      {/* ── Next Steps ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="next-steps">
          Next Steps
        </h2>
        <div className={styles.linkGrid}>
          <Link
            to="/documentation/streaming-guide"
            className={styles.linkCard}
          >
            <div className={styles.linkCardTitle}>Streaming Guide</div>
            <div className={styles.linkCardDesc}>
              Configuration, custom overlays with CSS status selectors,
              bidirectional messaging, and a full configurator example.
            </div>
          </Link>
          <Link to="/documentation/tutorial" className={styles.linkCard}>
            <div className={styles.linkCardTitle}>HTML Tutorial</div>
            <div className={styles.linkCardDesc}>
              The framework-free version — CDN script tag, no build step.
              Covers the same concepts without Angular overhead.
            </div>
          </Link>
          <Link
            to="/documentation/sdk-reference"
            className={styles.linkCard}
          >
            <div className={styles.linkCardTitle}>SDK Reference</div>
            <div className={styles.linkCardDesc}>
              Generate admission tokens server-side. Minimal, production,
              low-latency, and resilient token patterns.
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
